import axios from 'axios';
// Fetch items for a purchase invoice by id
export async function fetchPurchaseInvoiceItems(id) {
  const res = await api.get(`/api/purchase-invoices/${id}`);
  return res.data?.data?.items || [];
}

// Fetch items for a sale invoice by id
export async function fetchSaleInvoiceItems(id) {
  const res = await api.get(`/api/sale-invoices/${id}`);
  return res.data?.data?.items || [];
}

// Update payment status and paid amount for purchase invoice
export async function updatePurchaseInvoicePayment(id, payment_status, paid_amount) {
  const res = await api.put(`/api/purchase-invoices/update-payment/${id}`, { payment_status, paid_amount });
  return res.data;
}

// Update payment status and paid amount for sale invoice
export async function updateSaleInvoicePayment(id, payment_status, paid_amount) {
  const res = await api.put(`/api/sale-invoices/update-payment/${id}`, { payment_status, paid_amount });
  return res.data;
}

export async function fetchLedgerParties() {
  const res = await api.get('/api/ledger/parties');
  return res.data;
}

export async function fetchPartyLedger(partyId, partyIds = [], { from, to } = {}) {
  const params = [];
  if (partyIds.length > 1) params.push(`partyIds=${encodeURIComponent(partyIds.join(','))}`);
  if (from) params.push(`from=${encodeURIComponent(from)}`);
  if (to) params.push(`to=${encodeURIComponent(to)}`);
  const query = params.length ? `?${params.join('&')}` : '';
  const res = await api.get(`/api/ledger/party/${partyId}${query}`);
  return res.data;
}

// Fetch all purchase invoices (large limit so client-side filtering/pagination sees the full set)
export async function fetchPurchaseInvoices(params = {}) {
  const { page = 1, limit = 100000 } = params;
  const res = await api.get(`/api/purchase-invoices?page=${page}&limit=${limit}`);
  return res.data;
}

// Delete a purchase invoice (also removes its items, extras, and payment records)
export async function deletePurchaseInvoice(id) {
  const res = await api.delete(`/api/purchase-invoices/${id}`);
  return res.data;
}

// Fetch all sale invoices (large limit so client-side filtering/pagination sees the full set)
export async function fetchSaleInvoices(params = {}) {
  const { page = 1, limit = 100000 } = params;
  const res = await api.get(`/api/sale-invoices?page=${page}&limit=${limit}`);
  return res.data;
}

// Proforma invoices (PI)
export async function fetchNextProformaInvoiceNo() {
  const res = await api.get('/api/proforma-invoices/next-invoice-no');
  return res.data;
}

export async function fetchProformaInvoices() {
  const res = await api.get('/api/proforma-invoices');
  return res.data;
}

export async function fetchProformaInvoiceById(id) {
  const res = await api.get(`/api/proforma-invoices/${id}`);
  return res.data;
}

export async function createProformaInvoice(payload) {
  const res = await api.post('/api/proforma-invoices', payload);
  return res.data;
}

export async function updateProformaInvoice(id, payload) {
  const res = await api.put(`/api/proforma-invoices/${id}`, payload);
  return res.data;
}

export async function deleteProformaInvoice(id) {
  const res = await api.delete(`/api/proforma-invoices/${id}`);
  return res.data;
}

export async function updateProformaInvoiceStatus(id, status) {
  const res = await api.patch(`/api/proforma-invoices/${id}/status`, { status });
  return res.data;
}

// Fetch next sale invoice number (with prefix)
export async function fetchNextSaleInvoiceNo() {
  const res = await api.get('/api/sale-invoices/next-invoice-no');
  return res.data;
}

const api = axios.create({ baseURL: '/', withCredentials: true });

export async function fetchLoginCaptcha(){
  const res = await api.get('/api/auth/captcha');
  return res.data;
}
export async function login(role, identifier, password, captchaId, captchaAnswer){
  const res = await api.post('/api/auth/login', { role, identifier, password, captchaId, captchaAnswer });
  return res.data;
}
export async function session(){
  const res = await api.get('/api/auth/session');
  return res.data;
}
export async function logout(){
  const res = await api.post('/api/auth/logout');
  return res.data;
}
export async function fetchProducts(page, limit, q){
  let url = '/api/products';
  const params = [];
  if (page) params.push(`page=${encodeURIComponent(page)}`);
  if (limit) params.push(`limit=${encodeURIComponent(limit)}`);
  if (q) params.push(`q=${encodeURIComponent(q)}`);
  if (params.length) url += '?' + params.join('&');
  const res = await api.get(url);
  return res.data;
}

export async function fetchAllProducts(q){
  let url = '/api/products/all';
  if (q) url += `?q=${encodeURIComponent(q)}`;
  const res = await api.get(url);
  return res.data;
}

export async function fetchProductById(id, includeArchived = false){
  const res = await api.get(`/api/products/${id}${includeArchived ? '?includeArchived=1' : ''}`);
  return res.data;
}

export async function restoreProduct(productId){
  const res = await api.post(`/api/products/${productId}/restore`);
  return res.data;
}

export async function archiveProduct(productId){
  const res = await api.delete(`/api/products/${productId}`);
  return res.data;
}

export async function searchProductByBarcode(barcode){
  const res = await api.get(`/api/products/barcode/${encodeURIComponent(barcode)}`);
  return res.data;
}

export async function fetchDashboardStats(){
  const res = await api.get('/api/dashboard/stats');
  return res.data;
}

export async function fetchProductsStats(){
  const res = await api.get('/api/products/stats');
  return res.data;
}

// Bulk products CSV export (returns Blob)
export async function exportProductsCsv(){
  const res = await api.get('/api/products/bulk/export', { responseType: 'blob' });
  return res.data; // Blob
}

// Bulk products CSV import
export async function importProductsCsv(file){
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/api/products/bulk/import', form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
}

// Bulk variants CSV export (returns Blob)
export async function exportVariantsCsv(){
  const res = await api.get('/api/products/bulk/export-variants', { responseType: 'blob' });
  return res.data; // Blob
}

// Bulk variants CSV import
export async function importVariantsCsv(file){
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/api/products/bulk/import-variants', form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
}

// POS related API functions
export async function createPOSOrder(orderData){
  const res = await api.post('/api/pos/orders', orderData);
  return res.data;
}

export async function createCustomer(customerData){
  const res = await api.post('/api/customers', customerData);
  return res.data;
}

export async function searchCustomers(query){
  const res = await api.get(`/api/customers/search?q=${encodeURIComponent(query)}`);
  return res.data;
}

export async function getNextBillNumber(){
  const res = await api.get('/api/pos/next-bill-number');
  return res.data;
}

export async function fetchOrders(){
  const res = await api.get('/api/orders');
  return res.data;
}

export async function getSellerInfo(){
  const res = await api.get('/api/seller/info');
  return res.data;
}

export async function fetchUnits(){
  const res = await api.get('/api/units');
  return res.data;
}

export async function fetchCategories(){
  const res = await api.get('/api/categories');
  return res.data;
}

export async function fetchSellerCategories(){
  const res = await api.get('/api/categories/seller-categories');
  console.log('fetchSellerCategories response:', res.data);
  return res.data;
}

export async function fetchBrands(){
  const res = await api.get('/api/brands');
  return res.data;
}

export async function fetchSubCategories(categoryId){
  const res = await api.get(`/api/categories/${categoryId}/subcategories`);
  return res.data;
}

export async function fetchTaxes(){
  const res = await api.get('/api/taxes');
  return res.data;
}

export async function fetchStock(page, limit, q, filters = {}){
  let url = '/api/stock';
  const params = [];
  if (page) params.push(`page=${encodeURIComponent(page)}`);
  if (limit) params.push(`limit=${encodeURIComponent(limit)}`);
  if (q) params.push(`q=${encodeURIComponent(q)}`);
  if (filters.stockStatus) params.push(`stockStatus=${encodeURIComponent(filters.stockStatus)}`);
  if (filters.productStatus) params.push(`productStatus=${encodeURIComponent(filters.productStatus)}`);
  if (filters.categoryId) params.push(`categoryId=${encodeURIComponent(filters.categoryId)}`);
  if (filters.brandId) params.push(`brandId=${encodeURIComponent(filters.brandId)}`);
  if (filters.archiveStatus) params.push(`archiveStatus=${encodeURIComponent(filters.archiveStatus)}`);
  if (filters.sortBy) params.push(`sortBy=${encodeURIComponent(filters.sortBy)}`);
  if (filters.sortOrder) params.push(`sortOrder=${encodeURIComponent(filters.sortOrder)}`);
  if (params.length) url += '?' + params.join('&');
  const res = await api.get(url);
  return res.data;
}

export async function fetchStockStats(filters = {}){
  let url = '/api/stock/stats';
  const params = [];
  if (filters.categoryId) params.push(`categoryId=${encodeURIComponent(filters.categoryId)}`);
  if (filters.subcategoryId) params.push(`subcategoryId=${encodeURIComponent(filters.subcategoryId)}`);
  if (filters.taxId) params.push(`taxId=${encodeURIComponent(filters.taxId)}`);
  if (filters.productStatus) params.push(`productStatus=${encodeURIComponent(filters.productStatus)}`);
  if (filters.archiveStatus) params.push(`archiveStatus=${encodeURIComponent(filters.archiveStatus)}`);
  if (params.length) url += '?' + params.join('&');
  const res = await api.get(url);
  return res.data;
}

export async function updateStock(variantId, stock){
  const res = await api.put(`/api/stock/variant/${variantId}`, { stock });
  return res.data;
}

export async function updateProductField(productId, field, value){
  const res = await api.put(`/api/stock/product/${productId}`, { [field]: value });
  return res.data;
}

export async function createStockProduct(product){
  const res = await api.post('/api/stock/product', product);
  return res.data;
}

export async function updateVariantField(variantId, field, value){
  const res = await api.put(`/api/stock/variant/${variantId}`, { [field]: value });
  return res.data;
}

export async function createProductVariant(productId, variant){
  const res = await api.post(`/api/stock/product/${productId}/variant`, variant);
  return res.data;
}

export async function retireProductVariant(variantId){
  const res = await api.delete(`/api/stock/variant/${variantId}`);
  return res.data;
}

export async function uploadProductImage(productId, image){
  const formData = new FormData();
  formData.append('image', image);
  const res = await api.post(`/api/stock/product/${productId}/image`, formData);
  return res.data;
}

export async function uploadProductGallery(productId, images){
  const formData = new FormData();
  images.forEach(image => formData.append('images', image));
  const res = await api.post(`/api/stock/product/${productId}/gallery`, formData);
  return res.data;
}

export async function removeProductGalleryImage(productId, imageId){
  const res = await api.delete(`/api/stock/product/${productId}/gallery/${imageId}`);
  return res.data;
}

export async function fetchProductAttributes(categoryId){
  const res = await api.get(`/api/stock/attributes${categoryId ? `?categoryId=${encodeURIComponent(categoryId)}` : ''}`);
  return res.data;
}

export async function updateVariantAttributes(variantId, attributeValueIds){
  const res = await api.put(`/api/stock/variant/${variantId}/attributes`, { attribute_value_ids: attributeValueIds });
  return res.data;
}

export async function uploadVariantImages(variantId, images){
  const formData = new FormData();
  images.forEach(image => formData.append('images', image));
  const res = await api.post(`/api/stock/variant/${variantId}/images`, formData);
  return res.data;
}

export async function removeVariantImage(variantId, imageId){
  const res = await api.delete(`/api/stock/variant/${variantId}/image/${imageId}`);
  return res.data;
}

export async function fetchProductCustomFields(){
  const res = await api.get('/api/stock/custom-fields');
  return res.data;
}

export async function fetchLanguages(){
  const res = await api.get('/api/stock/languages');
  return res.data;
}

export async function updateProductTranslation(productId, languageId, translation){
  const res = await api.put(`/api/stock/product/${productId}/translation/${languageId}`, translation);
  return res.data;
}

export async function uploadProductCustomFieldFile(productId, fieldId, file){
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post(`/api/stock/product/${productId}/custom-field/${fieldId}/file`, formData);
  return res.data;
}

export async function fetchParties(role){
  const res = await api.get(`/api/parties${role ? `?role=${encodeURIComponent(role)}` : ''}`);
  return res.data;
}

export async function createParty(party){
  const res = await api.post('/api/parties', party);
  return res.data;
}

export async function createConsignee(data){
  const res = await api.post('/api/consignees', data);
  return res.data;
}

export async function fetchConsignees(search){
  const url = `/api/consignees${search ? `?search=${encodeURIComponent(search)}` : ''}`;
  const res = await api.get(url);
  return res.data;
}

export async function createPurchaseInvoice(payload){
  const res = await api.post('/api/purchase-invoices', payload);
  return res.data;
}

export async function fetchSavedTransporters(){
  const res = await api.get('/api/purchase-invoices/transporters/list');
  return res.data;
}

export async function fetchSaleSavedTransporters(){
  const res = await api.get('/api/sale-invoices/transporters/list');
  return res.data;
}

export async function createSaleInvoice(payload){
  const res = await api.post('/api/sale-invoices', payload);
  return res.data;
}

export async function fetchProfitLoss(params = {}){
  const search = new URLSearchParams(params).toString();
  const res = await api.get(`/api/analytics/profit-loss${search ? `?${search}` : ''}`);
  return res.data;
}

export async function fetchInvoicesDashboardStats() {
  const res = await api.get('/api/invoices/dashboard/stats');
  return res.data;
}

export async function fetchOnlineOverviewStats() {
  const res = await api.get('/api/online-overview/stats');
  return res.data;
}

export default api;
